import React from 'react';
import BaseHeader from './BaseHeader';
import BaseBody from './BaseBody';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { KeyCode, DateUtil } from '../util';
import { getLangCode } from '@platform/api';

const DATE_SHOW_ROW_NUM = 6;
const DATE_PREV_NEXT_CHANGE_NUM = 1;

function DateHeader(props){
    const{
        viewDate,
        onPrevYear,
        onPrevMonth,
        onNextYear,
        onNextMonth,
        onMonthClick,
        onYearClick,
        locale,
    } = props;
    let year = DateUtil.getYear(viewDate);
    let month = DateUtil.getMonth(viewDate);
    if(getLangCode() === 'english'){ // 英文顺序也要换，先月后年
        month = DateUtil.toDayjs(DateUtil.addDate(viewDate, 0)).format('MMM');
    } else {
        year = year.toString().concat(locale["0000"]);
        month = month.toString().concat(locale["0001"]);
    }
    return (
        <BaseHeader
            onSuperPrev={onPrevYear}
            onPrev={onPrevMonth}
            onNext={onNextMonth}
            onSuperNext={onNextYear}
        >
            {// 英文顺序也要换，先月后年
                getLangCode() === 'english' ? 
                    <>
                        <button className="picker-month-btn" tabIndex="-1" onClick={onMonthClick}>{month}</button>
                        <button className="picker-year-btn" tabIndex="-1" onClick={onYearClick}>{year}</button>
                    </> : 
                    <>
                        <button className="picker-year-btn" tabIndex="-1" onClick={onYearClick}>{year}</button>
                        <button className="picker-month-btn" tabIndex="-1" onClick={onMonthClick}>{month}</button>
                    </>

            }
        </BaseHeader>
    );

}

function DateBody(props){
    const { viewDate, prefixColumn, weekStart, locale, renderCellContent } = props;

    let formatFun = function(date){
        return DateUtil.formatValue(date, { format: 'D' });
    };

    let tableHeader = [];
    if(prefixColumn){
        tableHeader.push(<th aria-label="empty cell"/>);
    }
    tableHeader = tableHeader.concat(DateUtil.getWeekHeader(locale, weekStart));
    let showStartDay = DateUtil.addDate(DateUtil.getMonthShowStart(viewDate), weekStart);
    let monthFirstDay = DateUtil.getMonthFirstDay(viewDate);
    if(DateUtil.isAfter(showStartDay, monthFirstDay)){
        showStartDay = DateUtil.addWeek(showStartDay, -1);
    }
    showStartDay = DateUtil.setTime(showStartDay, viewDate.getHours(), viewDate.getMinutes(), viewDate.getSeconds());
    return (
        <BaseBody 
            {...props}
            rowNum={DATE_SHOW_ROW_NUM} 
            colNum={DateUtil.DAYS_OF_WEEK} 
            firstShowDate={showStartDay}
            renderCellContent={renderCellContent || formatFun}
            calculateCellDate={DateUtil.addDate} 
            renderTableHeader={tableHeader}
        />
    );
}

class DatePanel extends React.Component{

    static defaultProps = {
        next: true,
        prev: true,
        superNext: true,
        superPrev: true,
    }

    _addDefaultTime = date => {
        const { picker, value } = this.props;
        if (picker === 'dateTime' && !value) {
            // 默认为当前时间
            let _year = date.getFullYear(), _month = date.getMonth(), _date = date.getDate();

            date = new Date();
            date.setFullYear(_year);
            date.setMonth(_month);
            date.setDate(_date);
        }
        return date;
    }

    handleDateSelect = (date, event) => {
        const { onChange, picker, onSelect } = this.props;

        date = this._addDefaultTime(date);

        onSelect?.(date, picker);
        if(picker === 'date'){
            onChange?.(date, event);
        }
    }

    handleYearChange = diff => {
        const { onPanelChange, viewDate } = this.props;
        let newDate = DateUtil.addYear(viewDate, diff);
        onPanelChange?.('date', newDate);
    }

    handleMonthChange = diff => {
        const { onPanelChange, viewDate } = this.props;
        let newDate = DateUtil.addMonth(viewDate, diff);
        onPanelChange?.('date', newDate);
    }

    onKeyDown = event => {
        const { viewDate, onSelect, onPanelChange, onChange } = this.props;
        // console.log('日期面板的按键事件', event.keyCode);
        let newDate = viewDate;
        switch(event.keyCode){
            case KeyCode.LEFT:
                if(event.ctrlKey){
                    newDate = DateUtil.addYear(viewDate, -1);
                } else {
                    newDate = DateUtil.addDate(viewDate, -1);
                }
                onSelect?.(newDate, 'date');
                break;
            case KeyCode.UP:
                newDate = DateUtil.addWeek(viewDate, -1);
                onSelect?.(newDate, 'date');
                break;
            case KeyCode.RIGHT:
                if(event.ctrlKey){
                    newDate = DateUtil.addYear(viewDate, 1);
                } else {
                    newDate = DateUtil.addDate(viewDate, 1);
                }
                onSelect?.(newDate, 'date');
                break;
            case KeyCode.DOWN:
                newDate = DateUtil.addWeek(viewDate, 1);
                onSelect?.(newDate, 'date');
                break;
            case KeyCode.HOME:
                newDate = DateUtil.getMonthFirstDay(viewDate);
                onSelect?.(newDate, 'date');
                break;
            case KeyCode.END:
                newDate = DateUtil.getMonthLastDay(viewDate);
                onSelect?.(newDate, 'date');
                break;
            case KeyCode.PAGE_UP:
                newDate = DateUtil.addMonth(viewDate, -1);
                onSelect?.(newDate, 'date');
                break;
            case KeyCode.PAGE_DOWN:
                newDate = DateUtil.addMonth(viewDate, 1);
                onSelect?.(newDate, 'date');
                break;
            case KeyCode.ENTER:
                onSelect?.(newDate, 'date');
                onChange?.(newDate, event);
                break;
            default: 
                return true;
        }
        event.preventDefault();
        if(!DateUtil.isSame(viewDate, newDate, 'month')){
            // console.log('月份变了', viewDate, newDate);
            onPanelChange?.('date', newDate);
        }
        return false;
    }

    render(){
        const {
            onPanelChange,
            next,
            superNext,
            prev,
            superPrev,
            onDateMouseEnter,
            onDateMouseLeave,
            panelWrapperProps,
            ...other
        } = this.props;
        const { viewDate, className } = other;
        return (
            <div 
                className={classnames({
                    "picker-panel-content": true,
                    "picker-date-panel": true,
                    "className": !!className,
                })}
                {...panelWrapperProps}
            >
                <DateHeader 
                    {...other}
                    onPrevYear={superPrev && this.handleYearChange.bind(this, -DATE_PREV_NEXT_CHANGE_NUM)} 
                    onNextYear={superNext && this.handleYearChange.bind(this, DATE_PREV_NEXT_CHANGE_NUM)}
                    onPrevMonth={prev && this.handleMonthChange.bind(this, -DATE_PREV_NEXT_CHANGE_NUM)} 
                    onNextMonth={next && this.handleMonthChange.bind(this, DATE_PREV_NEXT_CHANGE_NUM)}
                    onMonthClick={() => onPanelChange?.('month', viewDate)} 
                    onYearClick={() => onPanelChange?.('year', viewDate)}
                    viewDate={viewDate} 
                />
                <DateBody 
                    {...other} 
                    onSelect={this.handleDateSelect} 
                    onDateMouseEnter={date => {
                        date = this._addDefaultTime(date);
                        return onDateMouseEnter?.(date);
                    }}
                    onDateMouseLeave={date => {
                        date = this._addDefaultTime(date);
                        return onDateMouseLeave?.(date);
                    }}
                />
            </div>
        );
    }
}

DatePanel.propTypes = {
    next: PropTypes.bool,
    superNext: PropTypes.bool,
    prev: PropTypes.bool,
    superPrev: PropTypes.bool,
    picker: PropTypes.string,
    value: PropTypes.string,
    viewDate: PropTypes.object.isRequired,
    prefixColumn: PropTypes.func,
    weekStart: PropTypes.number.isRequired,
    locale: PropTypes.object.isRequired,
    onSelect: PropTypes.func,
    onChange: PropTypes.func,
    onPanelChange: PropTypes.func,
    onDateMouseEnter: PropTypes.func,
    onDateMouseLeave: PropTypes.func,
};

export default DatePanel;